import { shortMoney } from "./format";
import { useChartWidth } from "./use-chart-width";
import styles from "./accounting.module.css";

/** The widest a bar is drawn, in pixels. */
const MAX_BAR_WIDTH = 36;

/** One bar. `value` null is a period that has not happened yet — a dashed outline. */
export interface ChartBar {
  id: string;
  /** Under the bar. */
  label: string;
  value: number | null;
  /**
   * A wider, pale bar drawn behind the solid one — Expected behind Realized
   * (ER-D8). With a ghost, a null `value` draws the ghost alone, which is how
   * a day still ahead shows what is booked into it.
   */
  ghost?: number | null;
  selected?: boolean;
  /** What a hover and a screen reader say about it — the exact figure. */
  title: string;
}

/**
 * A nice step for `count` gridlines across `range`: 1, 2 or 5 times a power of
 * ten, so the axis reads $0, $20K, $40K rather than $0, $17,342, $34,684.
 */
export function niceStep(range: number, count: number): number {
  const raw = range / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
}

/**
 * Profit as bars — by year, by month or by day (STORY-172) — or Realized as
 * a solid bar inside Expected's pale one (STORY-180b).
 *
 * Hand-drawn SVG, as the approved prototype's `chart()` is, rather than a
 * charting library: there are three charts with one shape, and a dependency
 * would be more code than the chart.
 *
 * **One scale for the bars, the gridlines and the axis labels**, worked out
 * once below — so a bar cannot sit above the line that names its height. The
 * scale always includes zero, and a loss draws downward from it in the warm
 * colour, so profit and loss differ in direction as well as in colour.
 *
 * **Every bar is a button**: focusable, and Enter or Space picks it, so the
 * month that drives the day chart can be chosen without a mouse.
 */
export function ProfitChart({
  bars,
  currency,
  label,
  onPick,
  height = 170,
  showValues = true,
  labelSize = 12,
  testId,
}: {
  bars: readonly ChartBar[];
  currency: string;
  /** Names the chart for a screen reader. */
  label: string;
  onPick?: (id: string) => void;
  height?: number;
  /** The figure above each bar. Off for days, which would be thirty labels. */
  showValues?: boolean;
  labelSize?: number;
  testId?: string;
}): React.JSX.Element {
  // The real width of the card, so text keeps its size and the height is the height asked for.
  const [box, width] = useChartWidth();
  const padLeft = 64;
  const padRight = 8;
  const padTop = showValues ? 24 : 12;

  // Ghosts share the one scale, so a solid bar is always read against its own.
  const values = bars.flatMap((bar) =>
    [bar.value, bar.ghost ?? null].filter((v): v is number => v !== null),
  );
  let max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  // A loss's figure sits under its bar, so the lowest bar needs room below the
  // plot for it — without this a bar reaching the bottom tick writes its figure
  // over the month names.
  const padBottom = 26 + (showValues && min < 0 ? 18 : 0);
  // All zero (or nothing yet): a scale of one unit, so the axis still draws.
  if (max === min) max = 100;

  const step = niceStep(max - min, 4);
  const low = Math.floor(min / step) * step;
  const high = Math.ceil(max / step) * step;
  const y = (value: number): number =>
    padTop + ((high - value) / (high - low)) * (height - padTop - padBottom);

  const ticks: number[] = [];
  for (let tick = low; tick <= high + step / 2; tick += step) ticks.push(tick);

  const slot = (width - padLeft - padRight) / Math.max(1, bars.length);
  // At most this wide: twelve bars across a wide card are not meant to be slabs.
  const barWidth = Math.min(MAX_BAR_WIDTH, Math.max(3, slot * 0.6));
  const zero = y(0);

  return (
    <div ref={box}>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        role="group"
        aria-label={label}
        data-testid={testId}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className={Math.abs(tick) < step / 2 ? styles.zeroLine : styles.gridLine}
              x1={padLeft}
              x2={width - padRight}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className={styles.axisText}
              x={padLeft - 8}
              y={y(tick) + 4}
              fontSize={11}
              textAnchor="end"
            >
              {shortMoney(Math.abs(tick) < step / 2 ? 0 : tick, currency)}
            </text>
          </g>
        ))}

        {bars.map((bar, i) => {
          const x = padLeft + i * slot;
          const barX = x + (slot - barWidth) / 2;
          const pick = (): void => onPick?.(bar.id);
          const top = bar.value === null ? 0 : Math.min(y(bar.value), zero);
          const tall = bar.value === null ? 0 : Math.max(1, Math.abs(y(bar.value) - zero));
          const ghost = bar.ghost ?? null;
          // Inside a ghost the solid bar narrows, so both stay visible whichever is taller.
          const solidWidth = ghost === null ? barWidth : Math.max(2, barWidth * 0.5);

          return (
            <g
              key={bar.id}
              className={bar.selected === true ? `${styles.bar} ${styles.barOn}` : styles.bar}
              role="button"
              tabIndex={0}
              aria-label={bar.title}
              aria-pressed={bar.selected === true}
              onClick={pick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  pick();
                }
              }}
              data-testid={testId === undefined ? undefined : `${testId}-bar`}
            >
              <title>{bar.title}</title>
              <rect
                className={styles.barHit}
                x={x + 1}
                y={padTop - 18}
                width={Math.max(1, slot - 2)}
                height={height - padTop - padBottom + 18}
                rx={4}
              />
              {ghost !== null && (
                <rect
                  className={styles.barGhost}
                  x={barX}
                  y={Math.min(y(ghost), zero)}
                  width={barWidth}
                  height={Math.max(1, Math.abs(y(ghost) - zero))}
                  rx={2}
                />
              )}
              {bar.value === null ? (
                ghost === null && (
                  // Not started: an outline the height of a small bar, on the zero line.
                  <rect
                    className={styles.barFuture}
                    x={barX}
                    y={zero - 18}
                    width={barWidth}
                    height={18}
                  />
                )
              ) : (
                <>
                  <rect
                    className={bar.value < 0 ? styles.barLoss : styles.barProfit}
                    x={barX + (barWidth - solidWidth) / 2}
                    y={top}
                    width={solidWidth}
                    height={tall}
                    rx={2}
                  />
                  {showValues && (
                    <text
                      className={styles.barValue}
                      x={x + slot / 2}
                      y={bar.value < 0 ? top + tall + 13 : top - 6}
                      fontSize={11}
                      textAnchor="middle"
                    >
                      {shortMoney(bar.value, currency)}
                    </text>
                  )}
                </>
              )}
              <text
                className={styles.barLabel}
                x={x + slot / 2}
                y={height - 8}
                fontSize={labelSize}
                textAnchor="middle"
              >
                {bar.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
